import React, { useEffect, useImperativeHandle, useState, useRef } from 'react'

import { ChatMessage, IQuestionComponentProps, IQuestionComponentRef, QuestionViewFrame } from '../types'
import { IConversationResponseValidation, ConversationQuestionModelTypes, ConversationResponse } from './types'

import useTimeline from '../../modules/timeline/useTimeline'
import useSession from '../../session/useSession'
import QuestionFrame from '../_common/QuestionFrame/QuestionFrame'
import ConversationView from './View'
import PrequalView from './PrequalView'
import { doInnerPost } from '@aytm/app/swidget/base/modules/client/doPost'
import { IAction } from '@aytm/app/swidget/base/actions/types.ts'
import WebcamModule from '../../modules/webcam/Module'
import useSurveyLocale from '../../modules/locale/useSurveyLocale'

const ConversationQuestion = React.memo(
  React.forwardRef<IQuestionComponentRef, IQuestionComponentProps<ConversationQuestionModelTypes>>(({ model }, ref) => {
    const [[response, responseValidation, frame], setResponse] = useState<
      [ConversationResponse, IConversationResponseValidation, QuestionViewFrame]
    >([model.response, model.responseValidation, model.frame])
    const { t } = useSurveyLocale()
    const [showValidation, setShowValidation] = useState(false)
    const [innerError, setInnerError] = useState(false)
    const [agreed, setAgreed] = useState(false)
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [voices, setVoices] = useState<Blob[]>([])
    const [currentTopicId, setCurrentTopicId] = useState<string>(null)
    const mounted = useRef(false)

    const session = useSession()
    const timeline = useTimeline()
    const firstInit = useRef(null)
    const timeout = useRef(null)

    useEffect(() => {
      session.enableNext(responseValidation.valid || model.schema.optional)
    }, [session, responseValidation.valid, model.schema.optional])

    useEffect(() => {
      mounted.current = true

      return () => {
        mounted.current = false
      }
    }, [])

    useImperativeHandle<IQuestionComponentRef, IQuestionComponentRef>(ref, () => {
      return {
        tryNext() {
          if (model.response.approve && !agreed && !model.response.skip) {
            setAgreed(true)
            session.enableNext(false)
            //TODO: uncomment once back is ready
            // timeline.frameCommit(model.frame)
            // timeline.frameInteractionStart((model.frame as number) + 1)
            return true
          }
          if (model.responseValidation.valid || session.skipValidation || model.schema.optional) {
            return false
          } else {
            session.enableNext(false)
            return true
          }
        },
        tryPrev() {
          return false
        },
        showValidation() {
          setShowValidation(true)
        }
      }
    }, [
      model.response.approve,
      model.response.skip,
      model.responseValidation.valid,
      model.schema.optional,
      agreed,
      session
    ])

    const onUpdateResponse = (response: ConversationResponse) => {
      if (response.skip) {
        session.enableNext(true)
        model.responseValidation.valid = true
        model.response.skip = response.skip
        WebcamModule.stopStreams()
        session.doNext()
      }
      if (response.approve != null) {
        session.enableNext(true)
        model.responseValidation.valid = true
        model.response.approve = response.approve
      }
      setResponse([{ ...model.response }, model.responseValidation, model.frame])
    }

    const updateMessages = (messages: ChatMessage[]) => {
      if (messages && messages.length && mounted.current) {
        const lastMessage = messages[messages.length - 1]
        setMessages(messages)
        // eslint-disable-next-line no-use-before-define
        if ('next_message_job' == lastMessage?.status) timeout.current = setTimeout(() => getConversation(), 1000)
        // if ('request' == lastMessage?.status) setMessages(messages)
        if ('done' == lastMessage?.status) {
          session.enableNext(true)
          model.responseValidation.valid = true
          model.responseValidation.done = true
          setResponse([model.response, model.responseValidation, model.frame])
          // setMessages(messages)
        }
      }
    }

    const getConversation = () => {
      if (session.urls.conversations)
        void (async () =>
          await doInnerPost(
            session.urls.conversations,
            session.surveyMode
              ? {
                  proxy_params: session.action.action_proxy_params,
                  mode: session.surveyMode,
                  prettyError: true
                }
              : { proxy_params: session.action.action_proxy_params, prettyError: true }
          )
            .then(response => {
              if (response && response.json) return response.json()
              setInnerError(true)
              return {}
            })
            .then(response => {
              const newMessages = (response as IAction).data?.questions[0]?.messages
              updateMessages(newMessages)
              const lastMessage = newMessages[newMessages.length - 1]
              if (!currentTopicId) {
                setCurrentTopicId(lastMessage.id)
              } else if (
                lastMessage &&
                lastMessage.id &&
                (lastMessage.id != currentTopicId || 'done' == lastMessage.status)
              ) {
                session.progress.advanceQuestionSeconds(model.questionTime / model.schema.answers.length)
                setCurrentTopicId(lastMessage.id)
              }

              // setInnerError(true)
            })
            .catch(error => {
              // eslint-disable-next-line no-console
              console.log(error)
              setInnerError(true)
            }))()
    }

    const resetValidation = () => {
      setShowValidation(false)
    }

    const sendMessage = (message: string, blob?: Blob, token?: string, callback?: () => void) => {
      setShowValidation(false)
      setInnerError(false)
      if (session.urls.save_conversation)
        void (async () => {
          await doInnerPost(
            session.urls.save_conversation,
            session.surveyMode
              ? {
                  proxy_params: session.action.action_proxy_params,
                  mode: session.surveyMode,
                  message: message,
                  qref: model.schema.id,
                  prettyError: true,
                  audio_uid: token
                }
              : {
                  proxy_params: session.action.action_proxy_params,
                  message: message,
                  qref: model.schema.id,
                  prettyError: true,
                  audio_uid: token
                }
          )
            .then(response => {
              if (response && response.json) return response.json()
              setInnerError(true)
              if (callback) callback()
              return {}
            })
            .then(response => {
              if (response && 'error' == (response as IAction).status) {
                setInnerError(true)
                if (callback) callback()
                return
              }
              if (response) {
                const msgs = (response as IAction).data?.questions[0]?.messages || []
                if (blob) {
                  voices[msgs.length - 2] = blob
                  setVoices(voices)
                }
                updateMessages(msgs)
              }
              // if
            })
            .catch(error => {
              // eslint-disable-next-line no-console
              console.log(error)
              setInnerError(true)
              if (callback) callback()
            })
        })()
    }

    if (
      !firstInit.current && // console.log(timeout.current)
      // clearTimeout(timeout.current)
      ('audio' != model.schema.mode || agreed)
    ) {
      getConversation()
      firstInit.current = true
    }

    if ('audio' == model.schema.mode && !agreed) {
      return (
        <QuestionFrame model={model} titleText={t('questions', 'cn', 'question')}>
          <PrequalView showValidation={showValidation} onUpdateResponse={onUpdateResponse} />
        </QuestionFrame>
      )
    }

    return (
      <QuestionFrame model={model}>
        <ConversationView
          schema={model.schema}
          response={response}
          responseValidation={responseValidation}
          showValidation={showValidation}
          sendMessage={sendMessage}
          resetValidation={resetValidation}
          onUpdateResponse={onUpdateResponse}
          messages={messages}
          voices={voices}
          frame={frame}
          timeline={timeline}
          error={innerError}
        />
      </QuestionFrame>
    )
  }),
  (prevProps, nextProps) => prevProps.model === nextProps.model
)

ConversationQuestion.displayName = 'ConversationQuestion'

export default ConversationQuestion
